import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  AddressElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { fetchPaymentIntent, orderUpdate } from 'store/actions/order'
import * as appActions from 'store/actions/app'

// Stripe.js collects "advanced fraud signals" that feed its bot detection, which
// throws an hCaptcha challenge at confirmPayment when a session looks automated —
// exactly what QA's e2e runs look like (headless browser, same card/amount/IP in
// rapid succession). Disable the signals whenever we run on a TEST-mode key
// (pk_test_ = localhost/dev/staging) so automated runs don't get challenged;
// live keys (production) keep full fraud-signal collection. Must run before the
// first loadStripe() call; the setLoadParameters guard keeps us safe on older
// @stripe/stripe-js versions and under test mocks.
if (
  (process.env.REACT_APP_STRIPE_PUB_KEY || '').startsWith('pk_test_') &&
  typeof loadStripe.setLoadParameters === 'function'
) {
  loadStripe.setLoadParameters({ advancedFraudSignals: false })
}

// Deferred intent creation (BOTS-37): the PaymentIntent is created server-side only when
// the borrower submits — not on page load — so borrowers who load the page and leave don't
// leave abandoned PaymentIntents. The Element is mounted with mode/amount/currency instead
// of a client_secret; confirmPayment is given the freshly-created client_secret at submit.
const PaymentForm = ({ token, orderId, onSuccess, collectAtClose }) => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)
  // Borrower name + email from the schedule token (to_schedule_json2). Email + name back the
  // Payment Element's hidden ('never') fields via defaultValues; the AddressElement collects the
  // billing name + address shown to the borrower.
  const userEmail = useSelector(state => state.user.email)
  const userName = useSelector(state => state.user.name)

  // Card and ACH both require a full billing name + address (the Payment Element opts out of
  // address with 'never', so Stripe requires it at confirm for us_bank_account as well as card).
  // Since both methods need it, show the AddressElement up front for every method rather than
  // gating it per-selection — the borrower fills name + address, then picks a method.
  const showBillingAddress = true

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements || submitting) { return }

    setSubmitting(true)

    // 1. Validate the Payment Element before creating anything server-side.
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setSubmitting(false)
      dispatch(appActions.appErrors([submitError.message]))
      return
    }

    // 2. Create the intent now (deferred). For collect-at-close the server returns a
    //    SetupIntent (mode: 'setup') — save the card without charging; otherwise a
    //    PaymentIntent (mode: 'payment'). fetchPaymentIntent dispatches its own error +
    //    throws on failure, so just abort here.
    let intent
    try {
      intent = await dispatch(fetchPaymentIntent(token, orderId))
    } catch (err) {
      setSubmitting(false)
      return
    }

    // BOTS-48: the card is already saved for this collect-at-close payment (e.g. the borrower
    // came back) — nothing to confirm, just advance.
    if (intent.card_already_saved) {
      setSubmitting(false)
      onSuccess()
      return
    }

    const clientSecret = intent.client_secret
    const isSetup = intent.mode === 'setup'

    // Built from origin + pathname rather than the full window.location.href: the return path
    // doesn't read the query string or fragment, so there's no reason to forward whatever a
    // crafted link put there into a URL Stripe will navigate back to (APPSEC-279).
    const returnUrl = `${window.location.origin}${window.location.pathname}`

    // Stripe requires a value for any billingDetails field set to 'never' on the Element. When
    // the AddressElement is shown (Card) it collects name + address, so only email is passed
    // here. Otherwise (ACH/Link — no AddressElement) name + email are both passed (no address).
    const confirmParams = {
      // Deferred Elements + automatic_payment_methods (which allows redirects) requires a
      // return_url even with redirect: 'if_required'. Cards/Link confirm inline and never use
      // it; it's only hit if a redirect-based method is ever enabled (BOTS-30).
      return_url: returnUrl,
      payment_method_data: {
        billing_details: showBillingAddress
          ? { email: userEmail }
          : { name: userName, email: userEmail },
      },
    }

    // 3. Confirm the freshly-created intent with the collected payment details. confirmPayment/
    // confirmSetup can also *throw* (e.g. an IntegrationError) rather than resolve with
    // { error }; without catching it, the throw would skip setSubmitting(false) and strand
    // the button on "Processing...". Catch so we always reset and surface a message.
    let error
    let paymentIntent
    try {
      if (isSetup) {
        // Collect-at-close: save the card off-session (charged at close by
        // charge_pending_payments). No charge now, so no `processing` state to surface.
        ({ error } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams,
          redirect: 'if_required',
        }))
      } else {
        ({ error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams,
          redirect: 'if_required',
        }))
      }
    } catch (err) {
      error = err
    }
    setSubmitting(false)

    if (error) {
      dispatch(appActions.appErrors([error.message || 'Unable to process payment. Please try again.']))
    } else {
      // BOTS-29 (PAY-3): an ACH (us_bank_account) payment confirms as `processing` while
      // the bank verifies (a card confirms as `succeeded`). Flag it so the success page
      // shows the "your bank is verifying" message instead of "payment approved". Setup-mode
      // (collect-at-close) has no charge in flight, so this never applies.
      if (!isSetup && paymentIntent && paymentIntent.status === 'processing') {
        dispatch(orderUpdate({ payment_verifying: true }))
      }
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Approach B — per-method billing fields. The AddressElement (Stripe billing mode: full
          name + billing address) renders ONLY once Card is selected, so the borrower initially
          sees just the two method options; picking one expands its fields. It's mounted in the
          SAME Elements group as the PaymentElement, so confirmPayment({ elements }) auto-attaches
          its name + address to billing_details → charge.billing_details → CardInformation.
          For ACH/Link it stays hidden — ACH needs name + email (not address), supplied via the
          PaymentElement defaultValues (RENDER, so bank collection can start) and payment_method_data
          at confirm (Stripe's hard requirement for a 'never' field). The PaymentElement always
          suppresses its own address/name/email fields so it never duplicates the AddressElement's. */}
      {showBillingAddress && (
        <div style={{ marginBottom: '8px' }}>
          <AddressElement options={{ mode: 'billing' }} />
        </div>
      )}
      <PaymentElement
        options={{
          fields: { billingDetails: { address: 'never', name: 'never', email: 'never' } },
          defaultValues: { billingDetails: { name: userName, email: userEmail } },
        }}
      />
      <div
        className={submitting || !stripe ? 'button-disabled' : 'button'}
        onClick={!submitting && stripe ? handleSubmit : undefined}
        style={{ marginTop: '16px' }}
      >
        {submitting ? 'Processing...' : (collectAtClose ? 'Save Card' : 'Submit Payment')}
      </div>
    </form>
  )
}

const BorrowerPaymentForm = ({ activePayment, changeStep }) => {
  const token = useSelector(state => state.app.token)
  const orderId = useSelector(state => state.order.id)
  const paymentMethodTypes = useSelector(state => state.order.payment_method_types)
  // BOTS-48: collect-at-close — the order is in a status excluded from immediate charge, so the
  // Element saves the card (setup mode) instead of charging now; it's charged at close.
  const collectAtClose = useSelector(state => state.order.collect_at_close)
  const [stripe, setStripe] = useState(null)
  const [initError, setInitError] = useState(false)

  // Stripe.js is fetched from js.stripe.com at runtime; on a flaky connection or with a
  // content blocker it can fail, which previously left the borrower on a blank step (and the
  // failure as an unhandled rejection — Datadog issue edc962ba). Treat it as a recoverable
  // state with a "Try again" button. loadStripe() resets its internal promise and re-injects
  // the script on failure, so retrying it is a genuine reload. The PaymentIntent is NOT
  // created here (deferred to submit — BOTS-37), so there is nothing PI-related to retry.
  const initialize = useCallback(() => {
    setInitError(false)
    if (!stripe) {
      loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY)
        .then((stripeInstance) => {
          if (!stripeInstance) { throw new Error('Stripe.js unavailable') }
          setStripe(stripeInstance)
        })
        .catch(() => setInitError(true))
    }
  }, [stripe])

  // Mount-once.
  useEffect(() => {
    initialize()
  }, [])

  if (initError) {
    return (
      <div className="flex-container text-center">
        <div className="box fullwidth">
          <p style={{ marginBottom: '16px' }}>
            We were unable to load the payment form. Please check your connection and try again.
          </p>
          <div className="button" onClick={initialize} style={{ marginTop: '16px' }}>
            Try again
          </div>
        </div>
      </div>
    )
  }

  if (!stripe) { return null }

  // No collectable payment: getActivePayment found none (e.g. the borrower reopened a link
  // whose payment is already paid, deleted, or otherwise no longer outstanding). Guard before
  // reading activePayment.amount below — without this the options/amount access throws and the
  // page white-screens. Mirrors the legacy path, which only rendered a payment UI when an
  // activePayment existed.
  if (!activePayment) {
    return (
      <div className="flex-container text-center">
        <div className="box fullwidth">
          <p style={{ marginBottom: '16px' }}>
            This payment link is no longer active.
          </p>
        </div>
      </div>
    )
  }

  // amount is in cents and must match the server-side PaymentIntent amount
  // (create_payment_intent uses payment.amount, i.e. activePayment.amount). Stripe's
  // elements() requires a positive integer in the currency subunit and type-checks it,
  // but activePayment.amount can arrive as a string — so coerce to an int.
  // paymentMethodTypes must mirror create_payment_intent's server-side list (resolved
  // server-side, delivered in the schedule payload): the Element
  // mounts deferred (BOTS-37), before the PaymentIntent exists, so without an explicit
  // list it falls back to the Stripe Dashboard payment-method configuration — which
  // hides us_bank_account regardless of what the PI later allows (BOTS-29). When the
  // payload doesn't provide the list (older backend that creates PIs with
  // automatic_payment_methods), OMIT the key entirely so the Element stays on the
  // automatic config — passing an explicit list against an automatic PI is the same
  // hard confirmation mismatch.
  // BOTS-48: collect-at-close mounts the Element in 'setup' mode (save the card, no charge now —
  // charged at close via a SetupIntent). Setup mode takes no amount, and the collect-at-close
  // SetupIntent is card-only, so force ['card'] regardless of the lender's ACH config. The normal
  // flow mounts 'payment' mode with the amount and the lender's resolved method list.
  const appearance = { variables: { spacingGridRow: '8px' } }
  const options = collectAtClose
    ? {
        mode: 'setup',
        currency: 'usd',
        paymentMethodTypes: ['card'],
        appearance,
      }
    : {
        mode: 'payment',
        amount: parseInt(activePayment.amount, 10),
        currency: 'usd',
        // Match Stripe's internal field gap (spacingGridRow) to the inter-element margins below
        // (both 16px) so the whole form — email, name, address, card — is evenly spaced.
        appearance,
        ...(paymentMethodTypes && paymentMethodTypes.length ? { paymentMethodTypes } : {}),
      }

  const amountDisplay = (activePayment.up_to_amount
    ? activePayment.up_to_amount
    : activePayment.amount / 100).toFixed(2)

  return (
    <div className="flex-container text-center">
      <div className="box fullwidth">
        <p style={{ marginBottom: '16px' }}>
          {collectAtClose
            ? (
            <>
              Your appraisal fee is <b>${amountDisplay}</b>
              <br />
              Save your card now — it will be charged at closing.
            </>
              )
            : (
            <>
              Your payment is <b>${amountDisplay}</b>
              <br />
              All payments must be made before proceeding with the appraisal
            </>
              )}
        </p>
        <Elements stripe={stripe} options={options}>
          <PaymentForm token={token} orderId={orderId} onSuccess={changeStep} collectAtClose={collectAtClose} />
        </Elements>
      </div>
    </div>
  )
}

export default BorrowerPaymentForm
